import { _, api, config, helpers, mui, React, ts, ui, useSelector } from '_core';

type CreatePortfolioProps = {
  open: boolean;
  setOpen: (_open: boolean) => void;
  onPortfolioCreated: (_portfolio: ts.types.appPortfolio.Portfolio) => void;
};

type SelectedSection = {
  region: string;
  base_risk_model: ts.types.riskModel.RiskModel;
  factors_risk_model: ts.types.riskModel.RiskModel;
};

const CreatePortfolio = ({ open, setOpen, onPortfolioCreated }: CreatePortfolioProps) => {
  const riskModels = useSelector((state) => state.resources.risk_models);

  const [errorMessage, setErrorMessage] = React.useState('');

  const models = React.useMemo(() => {
    return config.enabledModels
      .map((model) => {
        const riskModel = riskModels.find(
          (rm) => rm.id === model.base_risk_model_id || rm.source_resource_id === model.base_risk_model_id
        );

        const factorsRiskModel = riskModels.find(
          (rm) => rm.id === model.factors_risk_model_id || rm.source_resource_id === model.factors_risk_model_id
        );

        if (riskModel && factorsRiskModel) {
          return {
            region: model.region,
            base_risk_model: riskModel,
            factors_risk_model: factorsRiskModel,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [config.enabledModels, riskModels]);

  const [name, setName] = React.useState('');
  const [selectedRiskModel, setSelectedRiskModel] = React.useState<SelectedSection>(models[0]);
  const [loadingCreate, setLoadingCreate] = React.useState(false);

  const handleClose = () => {
    setName('');
    setSelectedRiskModel(models[0]);
    setOpen(false);
  };

  const handleSubmit = async () => {
    setErrorMessage('');

    // Validation
    if (!name || !selectedRiskModel) {
      setErrorMessage('All attributes are required.');
      setLoadingCreate(false);
      return;
    }

    setLoadingCreate(true);

    const portfolioPayload = {
      name,
      handle: `portfolio_app_${helpers.gibberishGenerator.stringGenerator(10)}`,
      definition: {
        risk_model_id: selectedRiskModel.base_risk_model.id,
        style_risk_model_id: selectedRiskModel.factors_risk_model.id,
      },
    } as ts.types.appPortfolio.PortfolioDraft;

    try {
      const resp = await api.app_portfolios.create(portfolioPayload);
      handleClose();
      onPortfolioCreated(resp.data);
    } catch (err: any) {
      setErrorMessage(helpers.parseApiError(err));
    } finally {
      setLoadingCreate(false);
    }
  };

  const formContent = () => (
    <mui.core.Stack gap={2} pt={3}>
      <mui.core.TextField
        autoFocus
        id="name"
        label={'Portfolio name'}
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        variant="outlined"
        required
        size="small"
        sx={{ mb: 3 }}
        inputProps={{
          autocomplete: 'off',
        }}
      />

      <mui.core.FormControl fullWidth variant="outlined" size="small">
        <mui.core.InputLabel>Region</mui.core.InputLabel>
        <mui.core.Select
          label="Region"
          value={selectedRiskModel.region}
          onChange={(e) => setSelectedRiskModel(models.find((m) => m.region === e.target.value) || models[0])}
        >
          {models.map((model) => (
            <mui.core.MenuItem key={model.base_risk_model.id} value={model.region}>
              {model.region}
            </mui.core.MenuItem>
          ))}
        </mui.core.Select>
      </mui.core.FormControl>
    </mui.core.Stack>
  );

  const dialogContent = () => (
    <>
      <mui.core.DialogContent>{formContent()}</mui.core.DialogContent>

      <mui.core.DialogActions>
        <ui.Button
          variant="contained"
          color="primary"
          loading={loadingCreate}
          onClick={handleSubmit}
          style={{ margin: '0rem 1rem 1rem' }}
          fullWidth
          size="large"
        >
          Create Portfolio
        </ui.Button>
      </mui.core.DialogActions>
    </>
  );

  return (
    <mui.core.Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="xs"
      fullWidth
    >
      {errorMessage && (
        <mui.core.Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </mui.core.Alert>
      )}
      <ui.DialogTitle closeAction={handleClose}>{'Create Portfolio'}</ui.DialogTitle>

      {_.isEmpty(models) ? (
        <ui.EmptyMessage>
          Please ask the administrator to enable a region for you to create a portfolio.
        </ui.EmptyMessage>
      ) : (
        dialogContent()
      )}
    </mui.core.Dialog>
  );
};

export default CreatePortfolio;
